import {invalidateResource, mutateCache, readResource} from "api/resourceCache";
import {stopUsingPurchase} from "./purchaseUseIntent";
import {listingWithout, type PurchaseListing} from "./unusedPurchases";

/**
 * The cache key both surfaces read (kitecorp/transferify-ui#3004): the Billing card in the top bar's
 * sheet and the note on the create panel. ONE key, so a withdrawal that was refunded in the card removes the purchase from
 * the note at once, and a note that closes over a purchase is closing over the same list the card shows.
 * Cleared at logout with every other private resource (`clearResourceCache`).
 *
 * React-free, beside the intent store rather than in the hook, because the create flow has to reach both
 * from a callback that is not a hook call.
 */
export const UNUSED_PURCHASES_CACHE_KEY = "unused-purchases";

/**
 * A transfer was just created, so a purchase may have been spent on it (review of ui#3020, finding 2).
 *
 * Two things follow, and neither may wait for the cache's 30-second freshness window: the choice "Use
 * it" recorded is done with — a second over-cap send within the window would otherwise name the purchase
 * this one consumed and be refused at the init — and the listing is stale, so the next mount of the note
 * or the card re-reads it instead of offering a purchase the api has already marked CONSUMED.
 *
 * Called on the api's own answer that the transfer exists, not on the press: a refused init spends
 * nothing, and forgetting the choice there would take the strip away from a reader whose send did not go.
 */
export function noteTransferCreated(): void {
	stopUsingPurchase();
	invalidateResource(UNUSED_PURCHASES_CACHE_KEY);
}

/**
 * A withdrawal declaration came back (kitecorp/transferify-ui#3967), so the shared listing follows it.
 *
 * A purchase the api refunded and revoked leaves the listing at once, not on the next background read: the note on the
 * create panel reads the same list and must not keep offering a purchase the buyer has just withdrawn from. Every
 * other result re-reads the listing in the background, because each can mean the api's word moved: a declaration a
 * person settles leaves the purchase listed as it was, a failed one may have been committed by the api before the
 * answer was lost, and a purchase the api no longer lists must stop being painted. The rows stay painted meanwhile;
 * only the freshness is dropped.
 */
export function noteWithdrawalSettled(purchaseId: string, refunded: boolean): void {
	if (refunded) {
		// Nothing read yet means nothing painted to correct, and a patch would otherwise create an entry out of nothing.
		const listing = readResource<PurchaseListing>(UNUSED_PURCHASES_CACHE_KEY);
		if (listing !== undefined) {
			mutateCache<PurchaseListing>(UNUSED_PURCHASES_CACHE_KEY, () => listingWithout(listing, purchaseId));
		}
	} else {
		invalidateResource(UNUSED_PURCHASES_CACHE_KEY);
	}
}
